import type { UserInput } from "@intelligems/ig-types";

/**
 * What a generic cart-test row builds a cart FROM: a set of products, one or
 * more collections, or one or more tags. The kind decides which picker opens
 * and how the reply names the pick.
 */
export type CartPickKind = "products" | "collection" | "tag";

/** One picked thing, as every picker already exposes it. */
export interface CartPickItem {
  id: string;
  label: string;
}

/** A test cart is a smoke test, not an order; ten lines is plenty. */
export const MAX_CART_PICK_PRODUCTS = 10;

export type CartPickReply = Extract<UserInput, { type: "choice" }> & {
  label: string;
};

/**
 * A delivery selection the merchant can pin onto a cart run, so a rule keyed
 * on checkout delivery (pickup, a rate, transit days) can be lit from the
 * composer without typing the shape out. Each one is said in words the
 * cart-run prompt already understands; the agent writes the delivery block.
 */
export type CartDeliveryModifier = "pickup" | "standard" | "express";

export const CART_DELIVERY_MODIFIERS: {
  id: CartDeliveryModifier;
  label: string;
  clause: string;
}[] = [
  {
    id: "pickup",
    label: "As a pickup order",
    clause: "as a pickup order at checkout",
  },
  {
    id: "standard",
    label: "With standard shipping",
    clause: "with standard shipping selected at checkout (5–8 days)",
  },
  {
    id: "express",
    label: "With express shipping",
    clause: "with express shipping selected at checkout (1–2 days)",
  },
];

/** The clause to append to a cart sentence, or "" for no modifier. */
export const formatDeliveryClause = (
  delivery: CartDeliveryModifier | null | undefined
): string => {
  const modifier = CART_DELIVERY_MODIFIERS.find(({ id }) => id === delivery);
  return modifier === undefined ? "" : ` ${modifier.clause}`;
};

/** The message a suggested-cart row sends, with any delivery pinned to it. */
export const formatSuggestedCartRun = ({
  ruleName,
  scenarioName,
  delivery,
}: {
  ruleName: string;
  scenarioName: string;
  delivery?: CartDeliveryModifier | null;
}): string =>
  `Run the "${ruleName}" rule against the "${scenarioName}" cart${formatDeliveryClause(delivery)}.`;

const humanJoin = (labels: string[]): string => {
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length <= 3) {
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  }
  return `${labels.slice(0, 2).join(", ")} and ${labels.length - 2} more`;
};

const quotedJoin = (labels: string[]): string =>
  humanJoin(labels.map((label) => `"${label}"`));

/**
 * The message a picker's Done sends.
 *
 * `choice` is what the model reads: the pick as ids, one per line, so the agent
 * resolves exactly what the merchant chose and never searches for a
 * substitute. `label` is what the transcript shows: the same request in the
 * merchant's terms, with no gid or wire identifier in it. Both name the rule,
 * because the rail can still be showing an earlier rule's conversation.
 *
 * `{type:"choice"}` is the existing transport for exactly this split —
 * `createUserMessage` and the server echo both render `choice` as the payload
 * and `label` as the bubble — and `readOpeningPrompt` accepts it, so a pick can
 * open an idle chat.
 */
export const formatCartPickReply = ({
  ruleName,
  kind,
  items,
  delivery,
}: {
  ruleName: string;
  kind: CartPickKind;
  items: CartPickItem[];
  /** A delivery selection pinned to the run; the clause rides on both renderings. */
  delivery?: CartDeliveryModifier | null;
}): CartPickReply => {
  const run = `Run the "${ruleName}" rule against a cart`;
  // The clause sits on the first line, ahead of the id list, so the model
  // reads the whole request before the lines it resolves.
  const clause = formatDeliveryClause(delivery);
  const lines = items.map((item) =>
    kind === "tag" ? `- tag: ${item.id}` : `- ${item.id} — ${item.label}`
  );
  const labels = items.map((item) => item.label);
  const plural = items.length > 1;

  switch (kind) {
    case "products":
      return {
        type: "choice",
        choice: [`${run} built from these products${clause}:`, ...lines].join(
          "\n"
        ),
        label: `${run} with ${humanJoin(labels)}${clause}.`,
      };
    case "collection":
      return {
        type: "choice",
        choice: [
          `${run} built from products in ${plural ? "these collections" : "this collection"}${clause}:`,
          ...lines,
        ].join("\n"),
        label: `${run} of products from the ${quotedJoin(labels)} ${plural ? "collections" : "collection"}${clause}.`,
      };
    case "tag":
      return {
        type: "choice",
        choice: [
          `${run} built from products with ${plural ? "these tags" : "this tag"}${clause}:`,
          ...lines,
        ].join("\n"),
        label: `${run} of products tagged ${quotedJoin(labels)}${clause}.`,
      };
  }
};
